// Pure attribute helpers — no Firestore imports, unit-testable with
// `node --test` (see attrs.test.mjs). Schema in docs/plans/user-attributes.md:
// per-user definitions are advisory (recs + filters); the property's
// user_attributes map is an open store keyed by name with number|string values.

export const NUMBER = 'number';
export const STRING = 'string';
export const CHECKBOX = 'checkbox';
export const MAX_NAME_LEN = 64;

// Starter list (seeded once per user). All names are canonical style:
// lowercase, letters/digits/spaces only. knownKey aligns with the
// extraction _KNOWN_ATTRIBUTE_SPECS keys so the future auto-fill joins by
// exact key.
export const DEFAULT_ATTRIBUTES = [
  { name: 'bedrooms', type: NUMBER },
  { name: 'bathrooms', type: NUMBER },
  { name: 'toilets', type: NUMBER },
  { name: 'floor area m2', type: NUMBER, knownKey: 'floor_area' },
  { name: 'land area m2', type: NUMBER, knownKey: 'land_area' },
  { name: 'year built', type: NUMBER, knownKey: 'year_built' },
  { name: 'cladding', type: STRING, knownKey: 'cladding' },
  { name: 'roofing', type: STRING, knownKey: 'roofing' },
  { name: 'insulation', type: STRING, knownKey: 'insulation' },
  { name: 'building type', type: STRING, knownKey: 'building_type' },
];

/** Canonical attribute name: lowercase, letters/digits/spaces only (m² →
 * m2, punctuation stripped), whitespace collapsed. Returns null when empty
 * or over the length cap. */
export function normalizeName(raw) {
  const name = canonicalize(String(raw ?? ''));
  if (!name || name.length > MAX_NAME_LEN) return null;
  return name;
}

/** Canonical attribute value: numbers/booleans pass through; strings get
 * the same lowercase letters/digits/spaces-only style (may be empty). */
export function normalizeAttrValue(v) {
  if (typeof v !== 'string') return v;
  return canonicalize(v);
}

/** Lowercase, m² → m2, strip everything but a-z0-9 and spaces, collapse. */
function canonicalize(s) {
  return s
    .toLowerCase()
    .replace(/²/g, '2')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Sentence case for display: capitalize the first letter, leave the rest
 * (canonical form is already lowercase). */
export function displayLabel(raw) {
  const s = String(raw ?? '');
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** The type of a stored value — number|string|checkbox (the value itself is the type). */
export function canonicalType(value) {
  if (typeof value === 'boolean') return CHECKBOX;
  return typeof value === 'number' ? NUMBER : STRING;
}

/** Apply a value change to a usage-count map.
 * oldValue null/undefined = add; newValue null/undefined = delete.
 * Returns a NEW map; counts drop at 0. Keys are String(value) (Firestore map
 * keys are strings) — a numeric 3 and string "3" share a count, matching the
 * forgiving-match rule. */
export function applyValueDelta(values, oldValue, newValue) {
  const next = { ...(values || {}) };
  const bump = (v, d) => {
    if (v === null || v === undefined) return;
    const k = String(v);
    const c = (next[k] || 0) + d;
    if (c <= 0) delete next[k];
    else next[k] = c;
  };
  if (newValue === null || newValue === undefined) {
    bump(oldValue, -1);
  } else if (oldValue === null || oldValue === undefined || oldValue !== newValue) {
    bump(oldValue, -1);
    bump(newValue, 1);
  }
  return next;
}

/** Merge definitions from any number of sources ({name, type}) into one union.
 * type = 'number' only if EVERY source says number; any conflict degrades to
 * 'string' (permissive — text holds numbers too). allNumeric mirrors that for
 * filter operator selection. Order: first-seen. */
export function mergeDefinitions(...sources) {
  const byName = new Map();
  for (const src of sources) {
    for (const d of src || []) {
      if (!d || !d.name) continue;
      const t = d.type === NUMBER ? NUMBER : STRING;
      const e = byName.get(d.name) || { numeric: true };
      e.numeric = e.numeric && t === NUMBER;
      byName.set(d.name, e);
    }
  }
  return [...byName.entries()].map(([name, e]) => ({
    name,
    type: e.numeric ? NUMBER : STRING,
    allNumeric: e.numeric,
  }));
}

/** Which default definitions are missing from the user's existing names. */
export function seedWrites(existingNames) {
  const have = new Set(existingNames || []);
  return DEFAULT_ATTRIBUTES.filter((d) => !have.has(d.name));
}

/** The last `n` attributes set on a property's user_attributes map. Key order
 * IS the add order — every write appends via `{...attrs, [name]: v}` and
 * Firestore map fields round-trip in insertion order — so the last keys are
 * the most recently added. Returns [[name, value], ...]. */
export function recentAttributes(attrs, n = 2) {
  const entries = Object.entries(attrs || {});
  // slice(-0) is slice(0) — guard so n=0 means none, not all.
  return n > 0 ? entries.slice(-n) : [];
}

/** Human display of an attribute value: numbers get thousands separators,
 * booleans read as Yes/No, strings pass through. */
export function displayValue(v) {
  if (typeof v === 'number') return v.toLocaleString('en-NZ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v ?? '');
}
